// ---------------------------------------------------------------------------
// Browser audio engine for the live voice call.
//
//   MicCapture — getUserMedia → AudioWorklet → 16 kHz mono PCM16 chunks
//                (~40 ms each) + an RMS level for the orb. A speech GATE
//                (`setGate`) lets the overlay hold frames back while a
//                recording plays, yet still notice the user starting to talk
//                (local energy VAD) so the recording can pause and the mic
//                reopen — the user is never left without a way to say "stop".
//   PcmPlayer  — 24 kHz PCM16 chunks from the server → gapless scheduled
//                playback on an AudioContext, with an analyser for the orb
//                and `flush()` for barge-in (the user interrupted the AI).
//
// Everything here is plain Web Audio; no library. The worklet source is
// inlined as a Blob URL so Vite needs no special handling.
// ---------------------------------------------------------------------------

import { createTimeStretcher } from "./timeStretch";

export const MIC_SAMPLE_RATE = 16_000;
export const OUT_SAMPLE_RATE = 24_000;
/** How much audio we batch per socket frame (ms). */
const CHUNK_MS = 40;

// Speech gate ("vad" mode) — tuned for a laptop mic with the browser's echo
// cancellation on while a recording plays through the speakers.
/** Noise floor assumed on entering vad mode (RMS, full scale = 1). */
const VAD_INITIAL_FLOOR = 0.02;
/** Absolute RMS a chunk must exceed to count as loud, whatever the floor… */
const VAD_MIN_LOUD_RMS = 0.012;
/** …or this many times the running noise floor, whichever is higher. */
const VAD_FLOOR_RATIO = 3.5;
/** Consecutive loud ~40 ms chunks that count as speech (4 ≈ 160 ms). */
const VAD_SPEECH_CHUNKS = 4;
/** Chunks kept before the trigger so the first syllable survives the gate. */
const VAD_RING_CHUNKS = 3;
/** EMA weight of a non-speech chunk on the noise floor. */
const VAD_FLOOR_EMA = 0.05;
/** Warm-up after entering vad mode (~480 ms): EVERY chunk teaches the floor
 *  (fast EMA) and nothing can trigger — the recording's own opening through
 *  the speakers must not read as the user speaking. */
const VAD_WARMUP_CHUNKS = 12;
const VAD_WARMUP_EMA = 0.25;

export type MicGateMode = "open" | "vad";

const WORKLET_SOURCE = `
class PcmCaptureProcessor extends AudioWorkletProcessor {
  process(inputs) {
    const ch = inputs[0] && inputs[0][0];
    if (ch && ch.length) this.port.postMessage(ch.slice(0));
    return true;
  }
}
registerProcessor("xale-pcm-capture", PcmCaptureProcessor);
`;

function rms(samples: Float32Array): number {
  let sum = 0;
  for (let i = 0; i < samples.length; i++) sum += samples[i]! * samples[i]!;
  return Math.sqrt(sum / Math.max(1, samples.length));
}

/** Linear resample Float32 → Float32 at the target rate. */
function resample(input: Float32Array, fromRate: number, toRate: number): Float32Array {
  if (fromRate === toRate) return input;
  const ratio = fromRate / toRate;
  const outLen = Math.floor(input.length / ratio);
  const out = new Float32Array(outLen);
  for (let i = 0; i < outLen; i++) {
    const pos = i * ratio;
    const i0 = Math.floor(pos);
    const i1 = Math.min(i0 + 1, input.length - 1);
    const frac = pos - i0;
    out[i] = input[i0]! * (1 - frac) + input[i1]! * frac;
  }
  return out;
}

function floatToPcm16(input: Float32Array): ArrayBuffer {
  const buf = new ArrayBuffer(input.length * 2);
  const view = new DataView(buf);
  for (let i = 0; i < input.length; i++) {
    const s = Math.max(-1, Math.min(1, input[i]!));
    view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return buf;
}

export interface MicCaptureHandle {
  /** Stop sending frames (worklet keeps running so resume is instant). */
  setMuted(muted: boolean): void;
  /**
   * "open" (default): chunks flow to `onChunk` as they are captured.
   * "vad": chunks are held back; a local energy VAD watches for the user
   * speaking and, on ~160 ms of speech, flips the gate to "open", flushes the
   * last few held chunks through `onChunk` (so the first syllable is not
   * lost) and then calls `onSpeech` exactly once. Entering "vad" resets the
   * detector. Manual mute (`setMuted(true)`) drops everything regardless.
   */
  setGate(mode: MicGateMode, onSpeech?: () => void): void;
  /** Current input level 0..1 (smoothed) — the real level in vad mode too. */
  level(): number;
  stop(): void;
}

export async function startMicCapture(
  onChunk: (pcm16: ArrayBuffer) => void,
): Promise<MicCaptureHandle> {
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 },
  });
  const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioCtx) throw new Error("Web Audio is not available in this browser.");
  const ctx = new AudioCtx();
  await ctx.resume();
  const url = URL.createObjectURL(new Blob([WORKLET_SOURCE], { type: "application/javascript" }));
  try {
    await ctx.audioWorklet.addModule(url);
  } finally {
    URL.revokeObjectURL(url);
  }
  const source = ctx.createMediaStreamSource(stream);
  const node = new AudioWorkletNode(ctx, "xale-pcm-capture", { numberOfInputs: 1, numberOfOutputs: 0 });
  source.connect(node);

  let muted = false;
  let smoothed = 0;
  const inRate = ctx.sampleRate;
  const chunkSamples = Math.round((inRate * CHUNK_MS) / 1000);
  let pending: Float32Array[] = [];
  let pendingLen = 0;

  // Speech gate state (see MicCaptureHandle.setGate).
  let gate: MicGateMode = "open";
  let onSpeech: (() => void) | undefined;
  let floor = VAD_INITIAL_FLOOR;
  let loudRun = 0;
  let ring: ArrayBuffer[] = [];
  let warmupLeft = VAD_WARMUP_CHUNKS;

  const resetVad = () => {
    floor = VAD_INITIAL_FLOOR;
    loudRun = 0;
    ring = [];
    warmupLeft = VAD_WARMUP_CHUNKS;
  };

  node.port.onmessage = (ev: MessageEvent<Float32Array>) => {
    const frame = ev.data;
    const lvl = rms(frame) * 6;
    smoothed = smoothed * 0.8 + Math.min(1, lvl) * 0.2;
    if (muted) return;
    pending.push(frame);
    pendingLen += frame.length;
    if (pendingLen < chunkSamples) return;
    const merged = new Float32Array(pendingLen);
    let off = 0;
    for (const f of pending) {
      merged.set(f, off);
      off += f.length;
    }
    pending = [];
    pendingLen = 0;
    const encoded = floatToPcm16(resample(merged, inRate, MIC_SAMPLE_RATE));
    if (gate === "open") {
      onChunk(encoded);
      return;
    }

    // vad: hold the chunk back and decide whether the user started talking.
    const energy = rms(merged);
    if (warmupLeft > 0) {
      // Learn what "the recording playing" sounds like on this mic first.
      warmupLeft -= 1;
      floor = floor * (1 - VAD_WARMUP_EMA) + energy * VAD_WARMUP_EMA;
      ring.push(encoded);
      if (ring.length > VAD_RING_CHUNKS) ring.shift();
      return;
    }
    const loud = energy > Math.max(VAD_MIN_LOUD_RMS, floor * VAD_FLOOR_RATIO);
    if (loud) {
      loudRun += 1;
    } else {
      loudRun = 0;
      // Only non-speech chunks teach the noise floor (residual echo, fans…).
      floor = floor * (1 - VAD_FLOOR_EMA) + energy * VAD_FLOOR_EMA;
    }
    if (loudRun < VAD_SPEECH_CHUNKS) {
      ring.push(encoded);
      if (ring.length > VAD_RING_CHUNKS) ring.shift();
      return;
    }
    // Speech: reopen the gate, replay the pre-roll + this chunk, notify once.
    gate = "open";
    const notify = onSpeech;
    onSpeech = undefined;
    const preRoll = ring;
    ring = [];
    loudRun = 0;
    for (const c of preRoll) onChunk(c);
    onChunk(encoded);
    notify?.();
  };

  return {
    setMuted(m) {
      muted = m;
      if (m) {
        pending = [];
        pendingLen = 0;
      }
    },
    setGate(mode, cb) {
      gate = mode;
      onSpeech = mode === "vad" ? cb : undefined;
      if (mode === "vad") resetVad();
    },
    level: () => (muted ? 0 : smoothed),
    stop() {
      try {
        node.port.onmessage = null;
        node.disconnect();
        source.disconnect();
      } catch {
        /* already torn down */
      }
      stream.getTracks().forEach((t) => t.stop());
      void ctx.close().catch(() => undefined);
    },
  };
}

export interface PcmPlayerHandle {
  enqueue(pcm16: ArrayBuffer): void;
  /** Drop everything queued (barge-in). */
  flush(): void;
  /** Output level 0..1 for the orb. */
  level(): number;
  /** True while audio is scheduled ahead of now. */
  isPlaying(): boolean;
  /**
   * Playback tempo, pitch preserved (timeStretch.ts): 1 = as generated,
   * 1.15 = 15 % faster. Takes effect from the next chunk.
   */
  setRate(rate: number): void;
  rate(): number;
  stop(): void;
}

/** A pause this long in the incoming chunks ends the utterance: the stretcher's held-back tail is released. */
const TAIL_FLUSH_MS = 160;

export function createPcmPlayer(sampleRate = OUT_SAMPLE_RATE): PcmPlayerHandle {
  const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioCtx) throw new Error("Web Audio is not available in this browser.");
  const ctx = new AudioCtx({ sampleRate });
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 512;
  analyser.connect(ctx.destination);
  const data = new Uint8Array(analyser.fftSize);
  let nextTime = 0;
  let sources: AudioBufferSourceNode[] = [];
  const stretcher = createTimeStretcher(sampleRate);
  let tailTimer: number | null = null;

  const level = () => {
    analyser.getByteTimeDomainData(data);
    let sum = 0;
    for (let i = 0; i < data.length; i++) {
      const v = (data[i]! - 128) / 128;
      sum += v * v;
    }
    return Math.min(1, Math.sqrt(sum / data.length) * 4);
  };

  const schedule = (samples: Float32Array) => {
    if (!samples.length) return;
    const buffer = ctx.createBuffer(1, samples.length, sampleRate);
    buffer.getChannelData(0).set(samples);
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.connect(analyser);
    const startAt = Math.max(nextTime, ctx.currentTime + 0.03);
    src.start(startAt);
    nextTime = startAt + buffer.duration;
    sources.push(src);
    src.onended = () => {
      sources = sources.filter((s) => s !== src);
    };
  };
  const clearTail = () => {
    if (tailTimer !== null) {
      window.clearTimeout(tailTimer);
      tailTimer = null;
    }
  };

  return {
    enqueue(pcm16) {
      if (ctx.state === "suspended") void ctx.resume();
      const int16 = new Int16Array(pcm16.byteLength % 2 === 0 ? pcm16 : pcm16.slice(0, pcm16.byteLength - 1));
      if (!int16.length) return;
      const samples = new Float32Array(int16.length);
      for (let i = 0; i < int16.length; i++) samples[i] = int16[i]! / 0x8000;
      schedule(stretcher.push(samples));
      clearTail();
      if (stretcher.rate() > 1) {
        tailTimer = window.setTimeout(() => {
          tailTimer = null;
          schedule(stretcher.flush());
        }, TAIL_FLUSH_MS);
      }
    },
    flush() {
      clearTail();
      stretcher.reset();
      for (const s of sources) {
        try {
          s.stop();
        } catch {
          /* already ended */
        }
      }
      sources = [];
      nextTime = 0;
    },
    level,
    isPlaying: () => sources.length > 0 && nextTime > ctx.currentTime,
    setRate: (rate) => stretcher.setRate(rate),
    rate: () => stretcher.rate(),
    stop() {
      clearTail();
      for (const s of sources) {
        try {
          s.stop();
        } catch {
          /* already ended */
        }
      }
      sources = [];
      void ctx.close().catch(() => undefined);
    },
  };
}
